import React, { useState } from 'react';
import { Sun, Battery, Car, Thermometer, Home, Zap, ArrowLeftRight, TrendingUp, Cpu, Plus, ShieldCheck } from 'lucide-react';

const systemComponents = [
  {
    id: 'pv',
    icon: Sun,
    title: 'Photovoltaik',
    description: 'Solarmodule auf dem Dach',
    color: 'sun-orange',
    position: 'top',
    angle: 0
  },
  {
    id: 'battery',
    icon: Battery,
    title: 'Batteriespeicher',
    description: 'Energie für Tag und Nacht',
    color: 'sun-blue',
    position: 'top-right',
    angle: 60
  },
  {
    id: 'ev',
    icon: Car,
    title: 'Elektroauto / Wallbox',
    description: 'Laden mit Solarstrom',
    color: 'emerald',
    position: 'bottom-right',
    angle: 120
  },
  {
    id: 'grid',
    icon: Zap,
    title: 'Stromnetz',
    description: 'Dynamische Stromtarife',
    color: 'amber',
    position: 'bottom',
    angle: 180
  },
  {
    id: 'heatpump',
    icon: Thermometer,
    title: 'Wärmepumpe',
    description: 'Heizen mit Solarstrom',
    color: 'rose',
    position: 'bottom-left',
    angle: 240
  },
  {
    id: 'smarthome',
    icon: Home,
    title: 'Smart Home',
    description: 'Intelligente Steuerung',
    color: 'violet',
    position: 'top-left',
    angle: 300
  }
];

const futureFeatures = [
  { icon: ArrowLeftRight, label: 'Bidirektionales Laden', sublabel: 'Vehicle-to-Grid / Vehicle-to-Home' },
  { icon: TrendingUp, label: 'Dynamische Stromtarife', sublabel: 'Günstig laden & speichern' },
  { icon: Cpu, label: 'Smart Energy Management', sublabel: 'Automatische Optimierung' },
  { icon: Plus, label: 'Erweiterbare Speicher', sublabel: 'Kapazität nach Bedarf' },
  { icon: ShieldCheck, label: 'Backup-Lösungen', sublabel: 'Notstromfähigkeit' }
];

const colorClasses = {
  'sun-orange': { bg: 'bg-sun-orange', light: 'bg-sun-orange/10', text: 'text-sun-orange', border: 'border-sun-orange/30', shadow: 'shadow-sun-orange/20' },
  'sun-blue': { bg: 'bg-sun-blue', light: 'bg-sun-blue/10', text: 'text-sun-blue', border: 'border-sun-blue/30', shadow: 'shadow-sun-blue/20' },
  'emerald': { bg: 'bg-emerald-500', light: 'bg-emerald-100', text: 'text-emerald-600', border: 'border-emerald-300', shadow: 'shadow-emerald-200' },
  'amber': { bg: 'bg-amber-500', light: 'bg-amber-100', text: 'text-amber-600', border: 'border-amber-300', shadow: 'shadow-amber-200' },
  'rose': { bg: 'bg-rose-500', light: 'bg-rose-100', text: 'text-rose-600', border: 'border-rose-300', shadow: 'shadow-rose-200' },
  'violet': { bg: 'bg-violet-500', light: 'bg-violet-100', text: 'text-violet-600', border: 'border-violet-300', shadow: 'shadow-violet-200' }
};

// Energy flow connections between components (curved paths)
const energyFlowConnections = [
  { from: 'pv', to: 'battery', color: '#f59e0b', label: 'Laden' },      // PV → Battery
  { from: 'pv', to: 'ev', color: '#10b981', label: 'Direktladen' },     // PV → EV
  { from: 'pv', to: 'grid', color: '#f59e0b', label: 'Einspeisung' },   // PV → Grid
  { from: 'battery', to: 'heatpump', color: '#3b82f6', label: 'Heizen' }, // Battery → Heatpump
  { from: 'ev', to: 'grid', color: '#eab308', label: 'V2G' }            // EV ↔ Grid (bidirectional)
];

// Helper to get component position by ID
const getComponentPosition = (id) => {
  const comp = systemComponents.find(c => c.id === id);
  if (!comp) return { x: 350, y: 350 };
  const angle = (systemComponents.indexOf(comp) * 60 - 90) * (Math.PI / 180);
  const radius = 250;
  return {
    x: 350 + radius * Math.cos(angle),
    y: 350 + radius * Math.sin(angle)
  };
};

const EnergySystemDiagram = () => {
  const [activeComponent, setActiveComponent] = useState(null);
  const [activeFlow, setActiveFlow] = useState(null);

  return (
    <section data-testid="energy-system-diagram" className="py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-sun-orange/10 text-sun-orange rounded-full text-sm font-semibold mb-4">
            Systemübersicht
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
            Das Sunprojects
            <span className="text-sun-orange"> Energiesystem</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Alle Komponenten arbeiten intelligent zusammen – für maximale Effizienz 
            und Unabhängigkeit. Entdecken Sie, wie Ihr Energiesystem funktioniert.
          </p>
        </div>

        {/* Desktop Circular Diagram */}
        <div className="hidden lg:block relative mx-auto" style={{ width: '700px', height: '700px' }}>
          {/* Connection Lines SVG */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 700 700">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="flowGradientOrange" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#f59e0b" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient id="flowGradientBlue" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient id="flowGradientGreen" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#10b981" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient id="flowGradientAmber" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#eab308" stopOpacity="0.6" />
                <stop offset="100%" stopColor="#eab308" stopOpacity="0.2" />
              </linearGradient>
              {/* Arrow marker for energy flow */}
              <marker id="arrowOrange" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#f59e0b" opacity="0.6" />
              </marker>
              <marker id="arrowBlue" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#3b82f6" opacity="0.6" />
              </marker>
              <marker id="arrowGreen" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#10b981" opacity="0.6" />
              </marker>
              <marker id="arrowAmber" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L6,3 L0,6 Z" fill="#eab308" opacity="0.6" />
              </marker>
            </defs>
            
            {/* Outer connection ring */}
            <circle
              cx="350"
              cy="350"
              r="250"
              fill="none"
              stroke="url(#lineGradient)"
              strokeWidth="1"
              strokeDasharray="4 8"
              className="opacity-20"
            />
            
            {/* Connection lines from center to each component */}
            {systemComponents.map((comp, index) => {
              const angle = (index * 60 - 90) * (Math.PI / 180);
              const radius = 250;
              const x2 = 350 + radius * Math.cos(angle);
              const y2 = 350 + radius * Math.sin(angle);
              return (
                <line
                  key={comp.id}
                  x1="350"
                  y1="350"
                  x2={x2}
                  y2={y2}
                  stroke="url(#lineGradient)"
                  strokeWidth="2"
                  strokeDasharray={activeComponent === comp.id ? "0" : "8 4"}
                  className={`transition-all duration-500 ${activeComponent === comp.id ? 'opacity-100' : 'opacity-30'}`}
                />
              );
            })}
            
            {/* Energy Flow Connections between components */}
            {energyFlowConnections.map((flow, index) => {
              const fromPos = getComponentPosition(flow.from);
              const toPos = getComponentPosition(flow.to);
              
              // Calculate control point for curved path (offset from center)
              const midX = (fromPos.x + toPos.x) / 2;
              const midY = (fromPos.y + toPos.y) / 2;
              const dx = toPos.x - fromPos.x;
              const dy = toPos.y - fromPos.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              
              // Curve outward from center
              const curveOffset = dist * 0.25;
              const perpX = -dy / dist * curveOffset;
              const perpY = dx / dist * curveOffset;
              
              // Determine if curve should go outward (away from center)
              const centerDist = Math.sqrt((midX - 350) ** 2 + (midY - 350) ** 2);
              const ctrlX = midX + (centerDist < 200 ? perpX : -perpX);
              const ctrlY = midY + (centerDist < 200 ? perpY : -perpY);
              
              // Shorten the path slightly so arrows don't overlap nodes
              const shortenFactor = 0.88;
              const startX = fromPos.x + (toPos.x - fromPos.x) * (1 - shortenFactor);
              const startY = fromPos.y + (toPos.y - fromPos.y) * (1 - shortenFactor);
              const endX = fromPos.x + (toPos.x - fromPos.x) * shortenFactor;
              const endY = fromPos.y + (toPos.y - fromPos.y) * shortenFactor;
              
              const gradientId = flow.color === '#f59e0b' ? 'flowGradientOrange' :
                                 flow.color === '#3b82f6' ? 'flowGradientBlue' :
                                 flow.color === '#10b981' ? 'flowGradientGreen' : 'flowGradientAmber';
              const arrowId = flow.color === '#f59e0b' ? 'arrowOrange' :
                              flow.color === '#3b82f6' ? 'arrowBlue' :
                              flow.color === '#10b981' ? 'arrowGreen' : 'arrowAmber';
              
              const isActive = activeFlow === index || 
                               activeComponent === flow.from || 
                               activeComponent === flow.to;
              
              return (
                <g key={`flow-${index}`}>
                  <path
                    d={`M ${startX} ${startY} Q ${ctrlX} ${ctrlY} ${endX} ${endY}`}
                    fill="none"
                    stroke={`url(#${gradientId})`}
                    strokeWidth={isActive ? "3" : "2"}
                    strokeDasharray="6 4"
                    markerEnd={`url(#${arrowId})`}
                    className={`transition-all duration-300 ${isActive ? 'opacity-80' : 'opacity-40'}`}
                    onMouseEnter={() => setActiveFlow(index)}
                    onMouseLeave={() => setActiveFlow(null)}
                    style={{ cursor: 'pointer' }}
                  />
                </g>
              );
            })}
          </svg>

          {/* Center Element */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-20">
            <div className="w-44 h-44 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-full flex items-center justify-center shadow-2xl border-4 border-sun-orange/30">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-2 bg-sun-orange rounded-full flex items-center justify-center">
                  <Cpu className="w-8 h-8 text-white" />
                </div>
                <div className="text-white font-bold text-sm">Sunprojects</div>
                <div className="text-sun-orange text-xs font-semibold">Energiesystem</div>
              </div>
            </div>
          </div>

          {/* Component Nodes */}
          {systemComponents.map((comp, index) => {
            const angle = (index * 60 - 90) * (Math.PI / 180);
            const radius = 250;
            const x = 350 + radius * Math.cos(angle);
            const y = 350 + radius * Math.sin(angle);
            const IconComponent = comp.icon;
            const colors = colorClasses[comp.color];
            const isActive = activeComponent === comp.id;

            return (
              <div
                key={comp.id}
                className="absolute z-10 transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                style={{ left: `${x}px`, top: `${y}px` }}
                onMouseEnter={() => setActiveComponent(comp.id)}
                onMouseLeave={() => setActiveComponent(null)}
              >
                <div className={`
                  w-28 h-28 rounded-2xl flex flex-col items-center justify-center
                  transition-all duration-300 border-2
                  ${isActive ? `${colors.light} ${colors.border} scale-110 shadow-xl ${colors.shadow}` : 'bg-white border-slate-100 shadow-lg hover:shadow-xl'}
                `}>
                  <div className={`w-12 h-12 ${colors.light} rounded-xl flex items-center justify-center mb-2 transition-all duration-300 ${isActive ? colors.bg : ''}`}>
                    <IconComponent className={`w-6 h-6 ${isActive ? 'text-white' : colors.text} transition-colors`} />
                  </div>
                  <span className="text-xs font-bold text-slate-900 text-center px-2 leading-tight">{comp.title}</span>
                </div>
                
                {/* Tooltip on hover */}
                <div className={`
                  absolute top-full left-1/2 -translate-x-1/2 mt-2 px-3 py-2 bg-slate-900 text-white text-xs rounded-lg whitespace-nowrap
                  transition-all duration-300 ${isActive ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2 pointer-events-none'}
                `}>
                  {comp.description}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-slate-900 rotate-45" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile/Tablet Stacked Layout */}
        <div className="lg:hidden">
          {/* Center Element */}
          <div className="flex justify-center mb-8">
            <div className="w-32 h-32 bg-gradient-to-br from-slate-800 via-slate-900 to-slate-800 rounded-full flex items-center justify-center shadow-xl border-4 border-sun-orange/30">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-1 bg-sun-orange rounded-full flex items-center justify-center">
                  <Cpu className="w-6 h-6 text-white" />
                </div>
                <div className="text-white font-bold text-xs">Sunprojects</div>
                <div className="text-sun-orange text-[10px] font-semibold">Energiesystem</div>
              </div>
            </div>
          </div>

          {/* Connection indicator */}
          <div className="flex justify-center mb-6">
            <div className="w-0.5 h-8 bg-gradient-to-b from-sun-orange to-transparent" />
          </div>

          {/* Components Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
            {systemComponents.map((comp) => {
              const IconComponent = comp.icon;
              const colors = colorClasses[comp.color];

              return (
                <div
                  key={comp.id}
                  className="bg-white rounded-2xl p-5 border border-slate-100 shadow-md hover:shadow-lg transition-all duration-300 hover:border-sun-orange/30"
                >
                  <div className={`w-12 h-12 ${colors.light} rounded-xl flex items-center justify-center mb-3`}>
                    <IconComponent className={`w-6 h-6 ${colors.text}`} />
                  </div>
                  <h4 className="font-bold text-slate-900 text-sm mb-1">{comp.title}</h4>
                  <p className="text-slate-500 text-xs">{comp.description}</p>
                </div>
              );
            })}
          </div>

          {/* Mobile Energy Flow Legend */}
          <div className="bg-gradient-to-r from-slate-50 to-white rounded-2xl p-6 border border-slate-200">
            <h4 className="font-bold text-slate-900 text-sm mb-4 text-center">Energieflüsse im System</h4>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-0.5 bg-gradient-to-r from-sun-orange to-sun-orange/30" style={{background: 'linear-gradient(90deg, #f59e0b 0%, rgba(245,158,11,0.3) 100%)'}}></div>
                <span className="text-xs text-slate-600">PV → Speicher / Netz</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-0.5" style={{background: 'linear-gradient(90deg, #10b981 0%, rgba(16,185,129,0.3) 100%)'}}></div>
                <span className="text-xs text-slate-600">PV → Elektroauto</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-0.5" style={{background: 'linear-gradient(90deg, #3b82f6 0%, rgba(59,130,246,0.3) 100%)'}}></div>
                <span className="text-xs text-slate-600">Speicher → Wärmepumpe</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-0.5" style={{background: 'linear-gradient(90deg, #eab308 0%, rgba(234,179,8,0.3) 100%)'}}></div>
                <span className="text-xs text-slate-600">E-Auto ↔ Netz (V2G)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Desktop Energy Flow Legend */}
        <div className="hidden lg:flex justify-center gap-6 mt-8 mb-8">
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
            <svg width="24" height="8" viewBox="0 0 24 8">
              <path d="M0,4 L20,4" stroke="#f59e0b" strokeWidth="2" strokeDasharray="4 2" />
              <path d="M18,1 L24,4 L18,7" fill="#f59e0b" opacity="0.6" />
            </svg>
            <span className="text-xs text-slate-600">Solarstrom</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
            <svg width="24" height="8" viewBox="0 0 24 8">
              <path d="M0,4 L20,4" stroke="#3b82f6" strokeWidth="2" strokeDasharray="4 2" />
              <path d="M18,1 L24,4 L18,7" fill="#3b82f6" opacity="0.6" />
            </svg>
            <span className="text-xs text-slate-600">Speicherstrom</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
            <svg width="24" height="8" viewBox="0 0 24 8">
              <path d="M0,4 L20,4" stroke="#10b981" strokeWidth="2" strokeDasharray="4 2" />
              <path d="M18,1 L24,4 L18,7" fill="#10b981" opacity="0.6" />
            </svg>
            <span className="text-xs text-slate-600">Direktladen</span>
          </div>
          <div className="flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-slate-200 shadow-sm">
            <svg width="24" height="8" viewBox="0 0 24 8">
              <path d="M0,4 L20,4" stroke="#eab308" strokeWidth="2" strokeDasharray="4 2" />
              <path d="M18,1 L24,4 L18,7" fill="#eab308" opacity="0.6" />
            </svg>
            <span className="text-xs text-slate-600">V2G / Netz</span>
          </div>
        </div>

        {/* Future Features Labels */}
        <div className="mt-16">
          <div className="text-center mb-8">
            <h3 className="text-xl font-bold text-slate-900">Zukunftssichere Technologien integriert</h3>
          </div>
          
          <div className="flex flex-wrap justify-center gap-3">
            {futureFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <div
                  key={index}
                  className="group flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-slate-50 to-white border border-slate-200 rounded-full hover:border-sun-orange/30 hover:shadow-md transition-all duration-300"
                >
                  <div className="w-9 h-9 bg-sun-orange/10 rounded-full flex items-center justify-center group-hover:bg-sun-orange transition-colors">
                    <IconComponent className="w-4 h-4 text-sun-orange group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 text-sm">{feature.label}</div>
                    <div className="text-slate-500 text-xs">{feature.sublabel}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-3 px-6 py-4 bg-slate-50 border border-slate-200 rounded-2xl">
            <div className="w-10 h-10 bg-sun-orange rounded-xl flex items-center justify-center">
              <Cpu className="w-5 h-5 text-white" />
            </div>
            <p className="text-slate-600 text-sm text-left">
              <span className="font-semibold text-slate-900">Mehr als nur Solarmodule.</span><br />
              Sunprojects plant ganzheitliche Energiesysteme für Ihr Zuhause.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EnergySystemDiagram;
